import { useMemo } from 'react';
import { type CampaignInfoType, CAMPAIGN_INFO_TYPES, useCampaignState } from 'features/campaigns';
import { useCampaignType, useCampaignTypeById } from '../../../../store/campaignsStore';
import { useCampaignInfoDisplay } from '../../../../hooks/info/useCampaignInfoDisplay';
import { testIds } from 'core/testIds';
import CampaignInfoLabel from './ui/CampaignInfoLabel';
import CampaignInfoValue from './ui/CampaignInfoValue';
import CountdownTimer from '../../CountdownTimer/CountdownTimer';

interface CampaignInfoItemProps {
  type: CampaignInfoType;
  iconSrc: string;
  label: string;
  value: string;
  suffix?: string;
  campaignId?: string;
}

const CampaignInfoItem: React.FC<CampaignInfoItemProps> = ({ type, iconSrc, label, value, suffix, campaignId }) => {
  const { collapsedAttributes } = useCampaignState();
  // Campaign type resolution mirrors CountdownTimer: by id when available,
  // falling back to the globally-selected type. Used only for test ids.
  const campaignTypeById = useCampaignTypeById(campaignId);
  const globalCampaignType = useCampaignType();
  const resolvedCampaignType = campaignTypeById ?? globalCampaignType;
  const {
    getCampaignInfoStyles,
    getColonForItem,
    shouldApplyCollapsedAttribute,
    getWrapperClassName,
    getDataAttributesByInfoType,
  } = useCampaignInfoDisplay(campaignId);

  const campaignInfoDisplayStyles = useMemo(() => getCampaignInfoStyles(), [getCampaignInfoStyles]);
  const colon = getColonForItem(type);
  const wrapperClassName = getWrapperClassName(type);
  const shouldCollapse = shouldApplyCollapsedAttribute(type);
  const conditionalAttributes = shouldCollapse ? collapsedAttributes : {};
  const dataAttributesByType = getDataAttributesByInfoType(type);

  // TIMER type: label + icon rendered normally, but the ticking value uses CountdownTimer (DOM ref updates)
  if (type === CAMPAIGN_INFO_TYPES.TIMER) {
    const isFinishedTimer = 'data-finished' in dataAttributesByType && dataAttributesByType['data-finished'] === 'true';

    return (
      <div
        className={wrapperClassName}
        {...conditionalAttributes}
        {...dataAttributesByType}
        data-testid={testIds.campaigns.timer(resolvedCampaignType ?? '')}
      >
        <CountdownTimer campaignId={campaignId}>
          {({ label: timerLabel, valueRef, initialValue, lessThanHour }) => (
            <>
              <CampaignInfoLabel
                iconSrc={iconSrc}
                label={timerLabel}
                colon={colon}
                styles={campaignInfoDisplayStyles}
                attributes={collapsedAttributes}
              />
              <CampaignInfoValue
                type={type}
                value={initialValue}
                styles={campaignInfoDisplayStyles}
                attributes={{
                  ...dataAttributesByType,
                  ...collapsedAttributes,
                  ...(lessThanHour !== undefined && !isFinishedTimer && { 'data-less-than-hour': String(lessThanHour) }),
                }}
                externalRef={valueRef}
              />
            </>
          )}
        </CountdownTimer>
      </div>
    );
  }

  return (
    <div className={wrapperClassName} {...conditionalAttributes} {...dataAttributesByType}>
      <CampaignInfoLabel
        iconSrc={iconSrc}
        label={label}
        colon={colon}
        styles={campaignInfoDisplayStyles}
        attributes={collapsedAttributes}
      />
      <CampaignInfoValue
        type={type}
        value={value}
        suffix={suffix}
        styles={campaignInfoDisplayStyles}
        attributes={{ ...dataAttributesByType, ...collapsedAttributes }}
      />
    </div>
  );
};

export default CampaignInfoItem;
